import {useCallback, useMemo, useRef, useState} from "react";
import {useMutation, useQueryClient} from "react-query";
import {useDebounce, useDeepCompareEffect} from "react-use";
import Alert from "react-s-alert";
import {
  TableUserDto,
  UserPermissionEnum,
} from "../../../../../utils/types/user";

import {UserService} from "../../../../../services/UserService";
import {buildResponseErrorMessage} from "../../../../../services/api/errorHelpers";

type Options = Readonly<{
  autoSave?: boolean;
}>;

export const isPermissionPendingChange = (
  saved: ReadonlyArray<UserPermissionEnum>,
  draft: ReadonlyArray<UserPermissionEnum>,
  permission: UserPermissionEnum,
): boolean => saved.includes(permission) !== draft.includes(permission);

export const countPermissionChanges = (
  saved: ReadonlyArray<UserPermissionEnum>,
  draft: ReadonlyArray<UserPermissionEnum>,
): number => {
  const savedSet = new Set(saved);
  const draftSet = new Set(draft);
  let count = 0;

  draftSet.forEach(permission => {
    if (!savedSet.has(permission)) {
      count += 1;
    }
  });

  savedSet.forEach(permission => {
    if (!draftSet.has(permission)) {
      count += 1;
    }
  });

  return count;
};

export const formatPendingChangesLabel = (count: number): string =>
  `${count} pending change${count === 1 ? "" : "s"}`;

export const useUserPermissionsMutation = (
  user: TableUserDto | undefined,
  {autoSave = true}: Options = {},
) => {
  const [activePermissions, setActivePermissions] = useState<
    ReadonlyArray<UserPermissionEnum>
  >([]);
  const [savedPermissions, setSavedPermissions] = useState<
    ReadonlyArray<UserPermissionEnum>
  >([]);
  const [isInitial, setIsInitial] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const syncedUserIdRef = useRef<string | undefined>();
  const hasLocalEditsRef = useRef(false);
  const queryClient = useQueryClient();

  const invalidateUserPermissionQueries = useCallback(
    (userId: string) => {
      queryClient.invalidateQueries(["useUserQuery", userId]);
      queryClient.invalidateQueries("usersPermissionQuery");
    },
    [queryClient],
  );

  const {mutate, isSuccess, isIdle} = useMutation(
    (permissions: ReadonlyArray<UserPermissionEnum>) =>
      UserService.putReportingPermissions(user!.id, permissions),
    {
      onSuccess: () => {
        if (user?.id) {
          invalidateUserPermissionQueries(user.id);
        }
      },
      onError: async (resp: any) => {
        Alert.warning(
          await buildResponseErrorMessage(
            resp,
            "There was an error while saving permissions",
          ),
        );
      },
      onSettled: () => {
        setIsSaving(false);
      },
    },
  );

  useDebounce(
    () => {
      if (autoSave && !isInitial && user?.id) {
        mutate(activePermissions);
      }
    },
    1000,
    [activePermissions, autoSave, isInitial, user?.id],
  );

  const onSetPermission = (permissions: ReadonlyArray<UserPermissionEnum>) => {
    setActivePermissions(permissions);

    if (autoSave) {
      setIsSaving(true);

      if (isInitial) {
        setIsInitial(false);
      }
    } else {
      hasLocalEditsRef.current = true;
    }
  };

  useDeepCompareEffect(() => {
    if (!user) {
      setIsInitial(true);
      setIsSaving(false);
      setActivePermissions([]);
      setSavedPermissions([]);
      syncedUserIdRef.current = undefined;
      hasLocalEditsRef.current = false;
      return;
    }

    const permissions = user.permissions ?? [];
    const userChanged = syncedUserIdRef.current !== user.id;

    if (autoSave) {
      setActivePermissions(permissions);

      if (isInitial) {
        setSavedPermissions(permissions);
      }

      syncedUserIdRef.current = user.id;
      setIsInitial(true);
      return;
    }

    if (userChanged) {
      syncedUserIdRef.current = user.id;
      hasLocalEditsRef.current = false;
      setActivePermissions(permissions);
      setSavedPermissions(permissions);
      setIsInitial(true);
      return;
    }

    if (!hasLocalEditsRef.current) {
      setActivePermissions(permissions);
      setSavedPermissions(permissions);
    }
  }, [autoSave, user?.id, user?.permissions]);

  const pendingChangeCount = useMemo(
    () =>
      autoSave
        ? 0
        : countPermissionChanges(savedPermissions, activePermissions),
    [activePermissions, autoSave, savedPermissions],
  );

  const isPermissionPending = useCallback(
    (permission: UserPermissionEnum) =>
      !autoSave &&
      isPermissionPendingChange(savedPermissions, activePermissions, permission),
    [activePermissions, autoSave, savedPermissions],
  );

  const save = useCallback(() => {
    if (!user?.id || pendingChangeCount === 0) {
      return;
    }

    setIsSaving(true);
    mutate(activePermissions, {
      onSuccess: () => {
        setSavedPermissions(activePermissions);
        hasLocalEditsRef.current = false;

        if (!autoSave) {
          Alert.success("Permissions saved successfully.");
        }
      },
    });
  }, [activePermissions, autoSave, mutate, pendingChangeCount, user?.id]);

  const discardChanges = useCallback(() => {
    setActivePermissions(savedPermissions);
    hasLocalEditsRef.current = false;
  }, [savedPermissions]);

  return {
    activePermissions,
    discardChanges,
    isPermissionPending,
    isSaving,
    isSuccess: isSuccess || isIdle,
    onSetPermission,
    pendingChangeCount,
    save,
    setActivePermissions,
  };
};
